import Title from "common/Typography/Title";
import {
  copyUrlToClipboard,
  getReferralUrl,
} from "features/affiliation/common/utils";
import styled, { useTheme } from "styled-components";
import CopyLink from "common/CopyToClipboard/CopyLink";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import AnimatedCheckIcon from "common/Animations/AnimatedCheckIcon";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { Copy } from "lucide-react";
import Button from "common/Buttons/Button";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

import { LanguageNameSpaces } from "@shared/types";

interface ShareStepProps {
  referralCode?: string;
}

const ShareStep = ({ referralCode }: ShareStepProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const referralLink = getReferralUrl(referralCode);
  const { isNewLook } = useTheme();

  // New look: the link in a field with "Copy" as the step's one primary
  // action.
  if (isNewLook)
    return (
      <Wrapper>
        {referralCode && (
          <>
            <AnimatedCheckIcon size={48} />
            <NewLookTitle>{t("links.link_created")}</NewLookTitle>
            <NewLookDescription>
              {t("links.share_description")}
            </NewLookDescription>
            <LinkField>
              <LinkText title={referralLink}>{referralLink}</LinkText>
              <Button
                type="primary"
                onClick={() => copyUrlToClipboard(referralLink)}
              >
                <Copy size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
                {t("common.copy")}
              </Button>
            </LinkField>
          </>
        )}
      </Wrapper>
    );

  return (
    <Wrapper>
      {referralCode && (
        <>
          <AnimatedCheckIcon size={100} />
          <Title level={2} style={{ margin: "12px 0 8px 0" }}>
            {t("links.link_created")}
          </Title>
          <ShareDescription level={4} $isMobile={isMobile}>
            {t("links.share_description")}
          </ShareDescription>
          <CopyLink
            url={referralLink}
            linkWidth={275}
            linkName={t("links.referral_link")}
          />
        </>
      )}
    </Wrapper>
  );
};

export default ShareStep;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const ShareDescription = styled(Title)<{ $isMobile: boolean }>`
  width: ${(p) => (p.$isMobile ? "100%" : "75%")};
  text-align: center;
  margin: 16px 0;
`;
const NewLookTitle = styled.div`
  margin-top: 12px;
  ${newLookType.panelTitle};
  font-size: 16px;
  line-height: 24px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  text-align: center;
`;
const NewLookDescription = styled.p`
  max-width: 360px;
  margin: 4px 0 20px;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  text-align: center;
`;
const LinkField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;
const LinkText = styled.span`
  flex: 1;
  min-width: 0;
  height: 32px;
  padding: 0 12px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  ${newLookType.body};
  line-height: 32px;
  color: ${(p) => getNewLookTokens(p.theme).text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
